import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Gift, Loader2, CheckCircle, XCircle, Clock, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { previewPromo, activatePromo } from '../features/payment/services/promoApi'

/**
 * PromoCode — страница промокода для deep-link переходов
 * Принимает :code из URL параметра
 * Показывает информацию о промокоде и позволяет активировать
 */
const PromoCode = () => {
  const { code } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [promo, setPromo] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isActivating, setIsActivating] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Загрузка превью промокода при монтировании
   */
  useEffect(() => {
    let cancelled = false

    const loadPromo = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const data = await previewPromo(code)
        if (!cancelled) {
          setPromo(data)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'error backend')
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    if (code) {
      loadPromo()
    }

    return () => {
      cancelled = true
    }
  }, [code])

  /**
   * Обработка активации промокода
   */
  const handleActivate = async () => {
    if (isActivating) return

    setIsActivating(true)
    try {
      await activatePromo(code)
      window.sessionStorage.removeItem('skinoz_pending_promo')
      toast.success(t('promo.success'))

      // Перезагружаем превью для обновления статуса
      const updated = await previewPromo(code)
      setPromo(updated)
    } catch (err) {
      // Показываем ошибку через toast
      const errorMap = {
        'promo not found': 'promo.errors.notFound',
        'promo expired': 'promo.errors.expired',
        'promo already activated': 'promo.errors.alreadyActivated',
        'promo limit reached': 'promo.errors.limitReached',
        'error backend': 'promo.errors.backend'
      }
      const key = errorMap[err.message] || 'promo.errors.backend'
      toast.error(t(key))
    } finally {
      setIsActivating(false)
    }
  }

  /**
   * Переход на страницу пополнения
   */
  const goToPayment = () => {
    navigate('/payment')
  }

  // Состояние загрузки
  if (isLoading) {
    return (
      <div className="min-h-[60vh] text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={48} className="animate-spin text-accent-primary" />
          <p className="text-text-secondary">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Ошибка загрузки
  if (error && !promo) {
    return (
      <div className="min-h-[60vh] text-white flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center">
          <div className="w-16 h-16 bg-red-900/20 border border-red-500/30 rounded-xl flex items-center justify-center">
            <XCircle size={32} className="text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white">{t('promo.title')}</h1>
          <p className="text-text-secondary">{t('promo.status.notFound')}</p>
          <button
            onClick={goToPayment}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('promo.deposit')}
          </button>
        </div>
      </div>
    )
  }

  // Промокод не найден
  if (!promo) {
    return (
      <div className="min-h-[60vh] text-white flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center">
          <div className="w-16 h-16 bg-gray-700 rounded-xl flex items-center justify-center">
            <Gift size={32} className="text-text-secondary" />
          </div>
          <h1 className="text-xl font-bold text-white">{t('promo.title')}</h1>
          <p className="text-text-secondary">{t('promo.status.notFound')}</p>
          <button
            onClick={goToPayment}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('promo.deposit')}
          </button>
        </div>
      </div>
    )
  }

  // Определение иконки и цвета по статусу
  const getStatusVisual = (status) => {
    switch (status) {
      case 'can_activate':
        return { Icon: Gift, color: 'text-accent-primary', bg: 'from-accent-primary to-accent-secondary' }
      case 'already_active':
        return { Icon: CheckCircle, color: 'text-accent-primary', bg: 'from-accent-primary to-accent-secondary' }
      case 'used':
        return { Icon: XCircle, color: 'text-gray-400', bg: 'from-gray-600 to-gray-700' }
      case 'expired':
        return { Icon: Clock, color: 'text-red-400', bg: 'from-red-500 to-red-600' }
      case 'activation_closed':
        return { Icon: XCircle, color: 'text-gray-400', bg: 'from-gray-600 to-gray-700' }
      case 'limit_reached':
        return { Icon: XCircle, color: 'text-gray-400', bg: 'from-gray-600 to-gray-700' }
      default:
        return { Icon: Gift, color: 'text-accent-primary', bg: 'from-accent-primary to-accent-secondary' }
    }
  }

  const { Icon, color, bg } = getStatusVisual(promo.status)

  // Ключ локализации для статуса
  const statusKeyMap = {
    'can_activate': 'promo.status.canActivate',
    'already_active': 'promo.status.alreadyActive',
    'used': 'promo.status.used',
    'expired': 'promo.status.expired',
    'activation_closed': 'promo.status.activationClosed',
    'limit_reached': 'promo.status.limitReached'
  }
  const statusText = statusKeyMap[promo.status] ? t(statusKeyMap[promo.status]) : ''

  // Можно ли активировать
  const canActivate = promo.status === 'can_activate' && !isActivating
  // Можно ли пополнить (промокод уже активен)
  const canDeposit = promo.status === 'already_active'

  return (
    <div className="min-h-[60vh] text-white flex items-center justify-center p-6">
      <div className="flex flex-col items-center gap-4 max-w-sm w-full text-center">
        {/* Иконка */}
        <div className={`w-20 h-20 bg-gradient-to-br ${bg} rounded-2xl flex items-center justify-center`}>
          <Icon size={40} className="text-dark-primary" />
        </div>

        {/* Заголовок */}
        <h1 className="text-2xl font-bold text-white">{t('promo.title')}</h1>

        {/* Код промокода */}
        <div className="px-4 py-2 bg-surface border border-white border-opacity-10 rounded-lg">
          <span className="text-lg font-bold text-accent-primary uppercase tracking-wide">
            {promo.code}
          </span>
        </div>

        {/* Процент бонуса */}
        {promo.percent > 0 && (
          <p className="text-xl font-bold text-accent-primary">
            {t('promo.percentLabel', { percent: promo.percent })}
          </p>
        )}

        {/* Тип бонуса */}
        <p className="text-text-secondary text-sm">
          {promo.multi_deposit ? t('promo.multiDeposit') : t('promo.singleDeposit')}
        </p>

        {/* Статус */}
        <div className={`px-3 py-1.5 rounded-lg border border-white border-opacity-10 bg-surface`}>
          <span className={`text-sm font-semibold ${color}`}>{statusText}</span>
        </div>

        {/* Остаток времени */}
        {promo.minutes_left !== null && promo.minutes_left > 0 && (
          <div className="flex items-center gap-1.5 text-yellow-400 text-sm">
            <Clock size={14} />
            {t('promo.minutesLeft', { minutes: promo.minutes_left })}
          </div>
        )}
        {promo.minutes_left === null && promo.status === 'already_active' && (
          <p className="text-text-secondary text-sm">{t('promo.timeless')}</p>
        )}

        {/* Кнопки действий */}
        <div className="w-full flex flex-col gap-3 mt-4">
          {canActivate && (
            <button
              onClick={handleActivate}
              disabled={isActivating}
              className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isActivating ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={18} className="animate-spin" />
                  {t('promo.activating')}
                </span>
              ) : (
                t('promo.activate')
              )}
            </button>
          )}

          {canDeposit && (
            <button
              onClick={goToPayment}
              className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              {t('promo.deposit')}
              <ArrowRight size={18} />
            </button>
          )}

          {/* Кнопка пополнения для неактивируемых статусов */}
          {!canActivate && !canDeposit && (
            <button
              onClick={goToPayment}
              className="w-full py-3 bg-surface border border-white border-opacity-10 text-white font-semibold rounded-lg hover:border-accent-primary/30 transition-all"
            >
              {t('promo.deposit')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PromoCode
